import React, {
  useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import classNames from 'classnames';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Autoformat,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TextTransformation,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

import Error from '@components/form/error';
import { useIsMount } from '@hooks/useIsMount';

const EDITOR_CONFIG = {
  plugins: [
    Autoformat,
    BlockQuote,
    Bold,
    Essentials,
    Heading,
    Image,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    MediaEmbed,
    Paragraph,
    PasteFromOffice,
    Table,
    TableToolbar,
    TextTransformation,
  ],
  toolbar: [
    'heading',
    '|',
    'bold',
    'italic',
    'link',
    'bulletedList',
    'numberedList',
    '|',
    'outdent',
    'indent',
    '|',
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    '|',
    'undo',
    'redo',
  ],
  image: {
    toolbar: ['imageStyle:inline', 'imageStyle:block', 'imageStyle:side', '|', 'imageTextAlternative'],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
};

export default function Editor({ label, ...props }) {
  const isMount = useIsMount();
  const [field, meta, helpers] = useField(props);
  const [value, setValue] = useState(field.value);

  const classes = useMemo(() => classNames(
    'input__wrapper',
    { error: meta.touched && !!meta.error },
  ), [field, meta]);

  useEffect(() => {
    let timer = null;
    if (!isMount) {
      timer = setTimeout(() => helpers.setValue(value), 1000);
    }

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className={classes}>
      {!!label && <label>{label}</label>}
      <CKEditor
        editor={ClassicEditor}
        config={EDITOR_CONFIG}
        data={field.value}
        onChange={(event, editor) => {
          setValue(editor.getData());
        }}
      />
      <Error name={field.name} />
    </div>
  );
}

Editor.defaultProps = {
  label: '',
};

Editor.propTypes = {
  label: PropTypes.string,
};
